import React, { useEffect, useState, useContext } from "react";
import { Card, Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import {
  FaClipboardList,
  FaChartLine,
  FaTshirt,
  FaShoppingCart,
  FaFileInvoice,
  FaTruck,
  FaExclamationTriangle,
} from "react-icons/fa";
import { FaMoneyBill1Wave } from "react-icons/fa6";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useNavigate } from "react-router-dom";

import "../styles/AdminDashboard.css";

function KPICard({ label, value, icon }) {
  return (
    <Card className="stat-card h-100">
      <Card.Body className="text-center">
        {icon && <div className="stat-icon mb-2">{icon}</div>}
        <div className="stat-number">{value}</div>
        <div className="stat-label">{label}</div>
      </Card.Body>
    </Card>
  );
}

function SalesChart({ data = [], labels = [] }) {
  const chartData = labels.map((label, i) => ({
    date: new Date(label).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    value: data[i],
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <LineChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="date" tick={{ fill: "#6b7280", fontSize: 12 }} />
        <YAxis tick={{ fill: "#6b7280", fontSize: 12 }} />
        <Tooltip />
        <Line
          type="monotone"
          dataKey="value"
          stroke="#4f46e5"
          strokeWidth={2.5}
          dot={{ r: 3 }}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// New component for operational task items
function OperationalTaskItem({ icon, label, count, variant = "primary" }) {
  const getVariantColor = (variant) => {
    const colors = {
      primary: { bg: "#e0e7ff", icon: "#4f46e5", color: "#4f46e5" },
      warning: { bg: "#fef3c7", icon: "#f59e0b", color: "#f59e0b" },
      danger: { bg: "#fee2e2", icon: "#ef4444", color: "#ef4444" },
      success: { bg: "#d1fae5", icon: "#10b981", color: "#10b981" },
      info: { bg: "#dbeafe", icon: "#3b82f6", color: "#3b82f6" },
    };
    return colors[variant] || colors.primary;
  };

  const colors = getVariantColor(variant);

  return (
    <div
      className="d-flex align-items-center justify-content-between p-3 mb-3 rounded"
      style={{
        backgroundColor: colors.bg,
        transition: "transform 0.2s, box-shadow 0.2s",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div className="d-flex align-items-center">
        <div
          className="d-flex align-items-center justify-content-center rounded-circle"
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: "white",
            color: colors.icon,
            fontSize: "18px",
          }}
        >
          {icon}
        </div>
        <span className="ms-3 fw-semibold" style={{ color: "#000000" }}>
          {label}
        </span>
      </div>
      <div
        style={{
          // backgroundColor: colors.color,
          color: "#000000",
          fontSize: "14px",
          padding: "6px 12px",
          fontWeight: "600",
        }}
      >
        {count}
      </div>
    </div>
  );
}

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function AdminDashboard() {
  const { userData } = useContext(AuthContext);
  const company_code = userData?.company_code;

  const [, setLoading] = useState(true);
  const [, setError] = useState("");

  const [kpis, setKpis] = useState([]);
  const [salesSeries, setSalesSeries] = useState([]);
  const [salesDates, setSalesDates] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [, setTotalOrderValueToday] = useState(0);
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  const [pendingPOsCount, setPendingPOsCount] = useState(0);
  const [pendingStylesCount, setPendingStylesCount] = useState(0);
  const [pendingGTNsCount, setPendingGTNsCount] = useState(0);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      if (!company_code) {
        setError("No company selected.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        // Fetch recent orders (for KPIs and sales series)
        const ordersRes = await axios.get(
          `${BASE_URL}/api/admin/issuing/orders`,
          {
            params: { company_code },
          }
        );

        const orders = Array.isArray(ordersRes.data)
          ? ordersRes.data
          : ordersRes.data.orders || [];

        // Compute KPIs from orders (basic): income MTD, orders today, pending orders, AOV
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        let incomeMTD = 0;
        // let ordersToday = 0;
        // let totalOrderCount = 0;
        // let totalOrderValue = 0;
        let totalTodayValue = 0;

        orders.forEach((o) => {
          const created = o.created_at ? new Date(o.created_at) : null;
          const amt = Number(o.total_amount || o.total || 0);
          // totalOrderValue += amt;
          // totalOrderCount += 1;
          if (created) {
            if (created >= startOfMonth) incomeMTD += amt;
            const isToday =
              created.toISOString().slice(0, 10) ===
              now.toISOString().slice(0, 10);
            // if (isToday) ordersToday += 1;
            if (isToday) totalTodayValue += amt;
          }
        });

        // pending orders count (derive from orders list)
        const pendingOrders = (orders || []).filter(
          (o) =>
            o.order_status && String(o.order_status).toLowerCase() === "pending"
        );
        setPendingOrdersCount(pendingOrders.length);

        setKpis([
          {
            label: "Today's Orders Value",
            value: `$${(totalTodayValue || 0).toFixed(2)}`,
            change: 0,
            icon: <FaMoneyBill1Wave style={{ color: "#ffffff" }} />,
          },
          {
            label: "Income (MTD)",
            value: `$${incomeMTD.toFixed(2)}`,
            change: 0,
            icon: <FaChartLine style={{ color: "#ffffff" }} />,
          },
        ]);

        setTotalOrderValueToday(totalTodayValue || 0);

        // Build sales series for last 30 days (sum by day)
        const lastNDays = 30;
        const series = [];
        const dates = [];
        for (let i = lastNDays - 1; i >= 0; i--) {
          const d = new Date();
          d.setDate(now.getDate() - i);
          const key = d.toISOString().slice(0, 10);
          dates.push(key);
          const daySum = orders
            .filter((o) => o.created_at && o.created_at.slice(0, 10) === key)
            .reduce((s, o) => s + Number(o.total_amount || 0), 0);
          series.push(Math.round(daySum));
        }
        setSalesSeries(series);
        setSalesDates(dates);

        // Fetch stock summary (use main-stock-summary endpoint)
        const stockRes = await axios.get(
          `${BASE_URL}/api/admin/stock/main-stock-summary`,
          {
            params: { company_code },
          }
        );
        const stockData = stockRes.data?.data || [];

        // Find low stock items (stock_qty low threshold heuristic)
        const low = (stockData || [])
          .filter((s) => Number(s.stock_qty) <= 5)
          .slice(0, 10)
          .map((s) => ({
            sku: s.sku,
            name: s.style_name || s.sku,
            stock: Number(s.stock_qty),
            reorderPoint: 5,
          }));
        setLowStock(low);

        // Fetch purchase orders and compute pending POs to approve
        try {
          const poRes = await axios.get(
            `${BASE_URL}/api/admin/po/get-purchase-orders`,
            {
              params: { company_code },
            }
          );
          let poData = [];
          if (Array.isArray(poRes.data)) poData = poRes.data;
          else if (Array.isArray(poRes.data.purchase_orders))
            poData = poRes.data.purchase_orders;
          else if (Array.isArray(poRes.data.data)) poData = poRes.data.data;
          const pendingPOs = (poData || []).filter(
            (p) => p.status && String(p.status).toLowerCase() === "pending"
          ).length;
          setPendingPOsCount(pendingPOs);
        } catch (poErr) {
          console.warn(
            "Failed to fetch purchase orders",
            poErr?.message || poErr
          );
          setPendingPOsCount(0);
        }

        // Fetch pending styles count
        try {
          const stylesRes = await axios.get(
            `${BASE_URL}/api/admin/styles/pending-approval-count`,
            {
              params: { company_code },
            }
          );
          const pendingStyles = stylesRes.data?.count || 0;
          setPendingStylesCount(pendingStyles);
        } catch (stylesErr) {
          console.error("Failed to fetch pending styles count:", stylesErr);
          console.error("Error details:", stylesErr?.response?.data);
          setPendingStylesCount(0);
        }

        // Fetch pending GTN count
        try {
          const gtnRes = await axios.get(
            `${BASE_URL}/api/admin/stock-allocation/get-pending-gtn-count`,
            {
              params: { company_code },
            }
          );
          const pendingGTNs = gtnRes.data?.pending_count || 0;
          setPendingGTNsCount(pendingGTNs);
        } catch (gtnErr) {
          console.error("Failed to fetch pending GTN count:", gtnErr);
          console.error("Error details:", gtnErr?.response?.data);
          setPendingGTNsCount(0);
        }
      } catch (err) {
        console.error("Dashboard fetch error", err);
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch dashboard data"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [company_code]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const getUserName = () => {
    if (userData?.name) {
      return `${userData.name}`;
    }
    return "Admin";
  };

  return (
    <Container fluid className="dashboard-container">
      <div className="dashboard-content">
        <Card className="dashboard-card">
          <Card.Body>
            <div className="welcome-section">
              <h1 className="welcome-title">
                {getGreeting()}, {getUserName()}!
              </h1>
            </div>

            <Row>
              {/* LEFT COLUMN: KPI Cards + Graph */}
              <Col lg={8} md={12}>
                {/* KPI Cards Row */}
                <Row className="mb-4">
                  {kpis.map((k, idx) => (
                    <Col
                      xs={12}
                      sm={6}
                      md={6}
                      lg={6}
                      className="mb-4"
                      key={idx}
                    >
                      <KPICard
                        label={k.label}
                        value={k.value}
                        change={k.change}
                        icon={k.icon}
                      />
                    </Col>
                  ))}
                </Row>

                {/* Sales Chart */}
                <Row>
                  <Col xs={12}>
                    <Card className="h-100">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0">Sales (Last 30 days)</h5>
                        </div>
                        <div className="p-3" style={{ minHeight: 120 }}>
                          <SalesChart data={salesSeries} labels={salesDates} />
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              </Col>

              {/* RIGHT COLUMN: Enhanced Operational Panel */}
              <Col lg={4} md={12} className="mb-4">
                <Card
                  className="h-full"
                  style={{
                    border: "none",
                    boxShadow: "4px 4px 4px 4px rgba(0,0,0,0.07)",
                  }}
                >
                  <Card.Body>
                    <div className="d-flex align-items-center mb-4">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                          width: "45px",
                          height: "45px",
                          backgroundColor: "#4f46e5",
                        }}
                      >
                        <FaClipboardList
                          style={{ color: "white", fontSize: "20px" }}
                        />
                      </div>
                      <div>
                        <h5
                          className="mb-0"
                          style={{ color: "#111827", fontWeight: "700" }}
                        >
                          Operational Panel
                        </h5>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div onClick={() => navigate("/warehouse/issuing")}>
                        <OperationalTaskItem
                          icon={<FaShoppingCart />}
                          label="Pending Orders"
                          count={pendingOrdersCount}
                          variant="primary"
                        />
                      </div>
                      <div onClick={() => navigate("/approve-po")}>
                        <OperationalTaskItem
                          icon={<FaFileInvoice />}
                          label="POs to Approve"
                          count={pendingPOsCount}
                          variant="info"
                        />
                      </div>
                      <div onClick={() => navigate("/style")}>
                        <OperationalTaskItem
                          icon={<FaTshirt />}
                          label="Products to Approve"
                          count={pendingStylesCount}
                          variant="success"
                        />
                      </div>
                      <div
                        onClick={() => navigate("/warehouse/allocate-stock/history")}>
                        <OperationalTaskItem
                          icon={<FaTruck />}
                          label="Pending GTNs"
                          count={pendingGTNsCount}
                          variant="warning"
                        />
                      </div>
                      <div onClick={() => navigate("/warehouse/stock")}>
                      <OperationalTaskItem
                        icon={<FaExclamationTriangle />}
                        label="Low Stock Items"
                        count={lowStock.length}
                        variant="danger"
                      />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}